/**
 * 杆子宝(干子宝)路单数据处理工具
 *
 * 参考旧项目: game-live/src/components/canvas/GanZiBaoRoadCanvas.vue
 *
 * 杆子宝游戏规则:
 * - 3行路单: 第一行=骰子1, 第二行=骰子2, 第三行=结果文字
 * - 数据格式: "31" = 干, "32" = 通, "33" = 1对, "34" = 花对
 * - 结果映射: { 31: [1, 2, 'gan_num'], 32: [2, 1, 'tong_num'], ... }
 *   - [第一个骰子值, 第二个骰子值, 多语言文本key]
 */

import { DICE_TREASURE_RESULT_MAP } from '@/constants'

/**
 * 路单单元格数据类型
 * - value: 原始值 (1, 2, 或多语言key如 'gan_num')
 */
export type GanZiBaoRoadCell = number | string

/**
 * 路单数据 (列数组)
 * 每列3行: [骰子1值, 骰子2值, 结果文字key]
 */
export type GanZiBaoRoadData = GanZiBaoRoadCell[][]

/**
 * 处理杆子宝主路数据
 *
 * 参考旧项目: game-live/src/utils/gameDetails.js -> processGzbMainRoad
 *
 * 数据格式:
 * - 输入: ["31", "32/33", "31/", "32/34"] (结果ID数组)
 *   - "31" → 只有结果ID
 *   - "32/33" → 赢家ID=32, 结果ID=33 (取33)
 *   - "31/" → 赢家ID=31, 无结果ID (取31)
 * - 输出: [[1, 2, 'gan_num'], [2, 1, 'tong_num'], ...]
 *
 * @param mainRoad - curShoesData 原始数据
 * @param columns - 显示列数 (默认30列)
 * @returns 处理后的路单数据 (二维数组)
 */
export function processGanZiBaoMainRoad(mainRoad: string[] = [], columns: number = 30): GanZiBaoRoadData {
  // 解析每一局的数据
  const list = mainRoad.map(item => {
    if (!item) return undefined

    // 处理斜杠分隔的数据: "赢家ID/结果ID"
    const [whoWin, whatN] = item.split('/')
    let resultIdStr: string

    if (whatN) {
      // 有第二部分,使用第二部分 (如: "32/33" → 使用 "33")
      resultIdStr = whatN
    } else if (whoWin) {
      // 没有第二部分,使用第一部分 (如: "31" 或 "31/" → 使用 "31")
      resultIdStr = whoWin
    } else {
      return undefined
    }

    const resultId = Number(resultIdStr)
    const result = DICE_TREASURE_RESULT_MAP[resultId]

    if (!result) return undefined

    // 返回: [骰子1值, 骰子2值, 多语言key] (创建新数组副本,避免引用共享)
    return [result[0], result[1], result[2]] as [number, number, string]
  })

  // 过滤掉 undefined，取最后 columns 列
  const filteredList = list.filter((item): item is [number, number, string] => item !== undefined).slice(-columns)

  // 直接返回数据,不添加占位符
  // Canvas 会从左往右绘制,右侧自动留空
  return filteredList
}

/**
 * 杆子宝统计数据类型
 */
export interface GanZiBaoStats {
  total: number // 总局数
  diff: number // 干 (31: 1正2反)
  same: number // 通 (32: 2正1反)
  pair1: number // 1对 (33: 2个正面)
  pairFlower: number // 花对 (34: 2个反面)
}

/**
 * 计算杆子宝统计数据
 *
 * 从原始数据中统计各结果的出现次数
 *
 * 统计规则:
 * - 31 (干): 只算干
 * - 32 (通): 只算通
 * - 33 (1对): 同时算通 + 1对
 * - 34 (花对): 同时算通 + 花对
 *
 * @param mainRoad - curShoesData 原始数据
 * @returns 统计数据
 */
export function calculateGanZiBaoStats(mainRoad: string[] = []): GanZiBaoStats {
  // 初始化统计数据
  const stats: GanZiBaoStats = {
    total: 0,
    diff: 0, // 干
    same: 0, // 通
    pair1: 0, // 1对
    pairFlower: 0, // 花对
  }

  // 遍历所有局数据,统计各结果出现次数
  mainRoad.forEach(item => {
    if (!item) return

    // 统计总局数 (有效数据)
    stats.total++

    // 处理斜杠分隔的数据: "赢家ID/结果ID"
    const [whoWin, whatN] = item.split('/')
    let resultIdStr: string

    if (whatN) {
      // 有第二部分,使用第二部分 (如: "32/33" → 使用 "33")
      resultIdStr = whatN
    } else if (whoWin) {
      // 没有第二部分,使用第一部分 (如: "31" 或 "31/" → 使用 "31")
      resultIdStr = whoWin
    } else {
      return
    }

    const resultId = Number(resultIdStr)

    // 统计各结果出现次数
    // 注意: 1对和花对也算"通",所以需要同时增加通的计数
    switch (resultId) {
      case 31:
        stats.diff++ // 干
        break
      case 32:
        stats.same++ // 通
        break
      case 33:
        stats.same++ // 1对也算通
        stats.pair1++ // 1对
        break
      case 34:
        stats.same++ // 花对也算通
        stats.pairFlower++ // 花对
        break
    }
  })

  return stats
}
